/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { clearApiCallHistory, getRecentApiCalls, setApiCallHistoryConfig } from './api-call-history-store';
import { DEFAULT_MAX_API_CALL_ENTRY_BYTES, DEFAULT_MAX_RECENT_API_CALLS } from './constants';
import { resolveRouteContextFromMatches } from './resolve-route-context-from-matches';
import { RouteSnapshotDispatchContext } from './route-snapshot-dispatch-context';
import { useCommandIntelligenceRoutePathname } from './use-command-intelligence-route-matches';

const AmbientCommandIntelligenceContext = createContext(null);

const EMPTY_ROUTE_SNAPSHOT = {
  matches: [],
  routeParams: {},
  pathname: '',
};

const normalizeAmbientContextConfig = ambientContext => {
  if (ambientContext === true) {
    return {
      maxRecentApiCalls: DEFAULT_MAX_RECENT_API_CALLS,
      maxEntryBytes: DEFAULT_MAX_API_CALL_ENTRY_BYTES,
    };
  }

  if (!ambientContext || typeof ambientContext !== 'object') {
    return null;
  }

  return {
    maxRecentApiCalls: ambientContext.maxRecentApiCalls ?? DEFAULT_MAX_RECENT_API_CALLS,
    maxEntryBytes: ambientContext.maxEntryBytes ?? DEFAULT_MAX_API_CALL_ENTRY_BYTES,
  };
};

export const AmbientCommandIntelligenceProvider = ({ children, enabled = false, ambientContext, appId }) => {
  const ambientConfig = useMemo(() => normalizeAmbientContextConfig(ambientContext), [ambientContext]);
  const isEnabled = enabled && Boolean(ambientConfig);
  const hookPathname = useCommandIntelligenceRoutePathname();
  const [routeSnapshot, setRouteSnapshot] = useState(EMPTY_ROUTE_SNAPSHOT);
  const pathname = routeSnapshot?.pathname || hookPathname;

  useEffect(() => {
    setApiCallHistoryConfig({
      enabled: isEnabled,
      maxRecentApiCalls: ambientConfig?.maxRecentApiCalls,
      maxEntryBytes: ambientConfig?.maxEntryBytes,
    });

    return () => {
      setApiCallHistoryConfig({ enabled: false });
    };
  }, [isEnabled, ambientConfig?.maxRecentApiCalls, ambientConfig?.maxEntryBytes]);

  const routeContext = useMemo(
    () =>
      resolveRouteContextFromMatches({
        matches: routeSnapshot?.matches,
        routeParams: routeSnapshot?.routeParams,
        pathname,
      }),
    [routeSnapshot?.matches, routeSnapshot?.routeParams, pathname]
  );

  const apiCallHistoryScopeKey = useMemo(() => {
    const entityRef = routeContext?.routeParams?.entityRef ?? '';

    return `${entityRef}::${routeContext?.pathname ?? ''}`;
  }, [routeContext?.routeParams?.entityRef, routeContext?.pathname]);

  useEffect(() => {
    clearApiCallHistory();
  }, [apiCallHistoryScopeKey]);

  const value = useMemo(
    () => ({
      enabled: isEnabled,
      appRef: appId,
      routeContext,
      getSnapshot: () => ({
        appRef: appId,
        routeContext,
        recentApiCalls: getRecentApiCalls(),
      }),
    }),
    [isEnabled, appId, routeContext]
  );

  return (
    <RouteSnapshotDispatchContext.Provider value={setRouteSnapshot}>
      <AmbientCommandIntelligenceContext.Provider value={value}>{children}</AmbientCommandIntelligenceContext.Provider>
    </RouteSnapshotDispatchContext.Provider>
  );
};

export const useAmbientCommandIntelligenceContext = () => {
  const context = useContext(AmbientCommandIntelligenceContext);

  if (!context) {
    return {
      enabled: false,
      appRef: '',
      routeContext: { pathname: '', routeHandle: {}, routeParams: {} },
      getSnapshot: () => ({
        appRef: '',
        routeContext: { pathname: '', routeHandle: {}, routeParams: {} },
        recentApiCalls: getRecentApiCalls(),
      }),
    };
  }

  return context;
};
